import ExcelJS from 'exceljs';
import JSZip from 'jszip';
import { DMF_ENTITIES, type MockState } from '@lg/d365-client';
import type { DocumentType } from '@lg/shared';
import { cellText, parseCsv } from '../people/excel.js';

/**
 * Mock-mode stand-in for D365 processing a people package: reads the CSVs
 * back out of the zip and writes them into mock D365 state, so the
 * post-provisioning checks run on data "in D365" (spec 12.3). Never used
 * against a real environment.
 */

type Row = Record<string, string>;

/** An entity's rows from the package: the Excel file D365 packages carry, or a CSV. */
async function readEntity(zip: JSZip, fileName: string): Promise<Row[]> {
  const xlsx = zip.file(fileName.replace(/\.csv$/i, '.xlsx'));
  const csv = zip.file(fileName);
  let table: string[][] = [];
  if (xlsx) {
    const wb = new ExcelJS.Workbook();
    await wb.xlsx.load(await xlsx.async('arraybuffer'));
    wb.worksheets[0]?.eachRow((row) => {
      const values = Array.isArray(row.values) ? row.values.slice(1) : [];
      table.push(values.map((v) => String(cellText(v as ExcelJS.CellValue) ?? '')));
    });
  } else if (csv) table = parseCsv(await csv.async('string'));
  const [header, ...rows] = table;
  if (!header) return [];
  return rows
    .filter((r) => r.some((c) => c !== ''))
    .map((r) => Object.fromEntries(header.map((h, i) => [h, r[i] ?? ''])));
}

export async function applyPeoplePackageToMock(
  bytes: Uint8Array,
  companyId: string,
  state: MockState,
): Promise<void> {
  const zip = await JSZip.loadAsync(bytes);
  const e = DMF_ENTITIES;
  const [users, workers, employments, positions, hierarchies, assignments, roleOrgs, limits] =
    await Promise.all([
      readEntity(zip, e.users.fileName),
      readEntity(zip, e.workers.fileName),
      readEntity(zip, e.employments.fileName),
      readEntity(zip, e.positions.fileName),
      readEntity(zip, e.positionHierarchies.fileName),
      readEntity(zip, e.positionAssignments.fileName),
      readEntity(zip, e.userRoleOrganizations.fileName),
      readEntity(zip, e.signingLimits.fileName),
    ]);

  for (const u of users) {
    const userId = u['USERID'] ?? '';
    if (!userId || state.users.some((x) => x.userId === userId)) continue;
    state.users.push({
      userId,
      upn: (u['ALIAS'] ?? '').toLowerCase(),
      name: u['USERNAME'] ?? userId,
      enabled: true,
    });
  }

  for (const w of workers) {
    const personnelNumber = w['PERSONNELNUMBER'] ?? '';
    if (!personnelNumber || state.workers.some((x) => x.personnelNumber === personnelNumber)) continue;
    const emp = employments.find((x) => x['PERSONNELNUMBER'] === personnelNumber);
    state.workers.push({
      personnelNumber,
      firstName: w['FIRSTNAME'] ?? '',
      lastName: w['LASTNAME'] ?? '',
      upn: (w['PRIMARYCONTACTEMAIL'] ?? '').toLowerCase() || null,
      companyId: emp?.['LEGALENTITYID'] ?? companyId,
      employmentStart: emp?.['EMPLOYMENTSTARTDATE'] ?? '',
    });
  }

  for (const p of positions) {
    const positionId = p['POSITIONID'] ?? '';
    if (!positionId) continue;
    const parent = hierarchies.find((h) => h['POSITIONID'] === positionId)?.['PARENTPOSITIONID'] ?? null;
    const holderNumber =
      assignments.find((a) => a['POSITIONID'] === positionId && a['ISPRIMARYPOSITION'] === 'Yes')?.[
        'PERSONNELNUMBER'
      ] ??
      assignments.find((a) => a['POSITIONID'] === positionId)?.['PERSONNELNUMBER'] ??
      null;
    const holder = holderNumber ? state.workers.find((w) => w.personnelNumber === holderNumber) : undefined;
    const record = {
      positionId,
      title: p['DESCRIPTION'] ?? '',
      jobId: p['JOBID'] ?? '',
      companyId,
      departmentId: p['DEPARTMENTNUMBER'] || null,
      reportsToPositionId: parent || null,
      holderPersonnelNumber: holderNumber,
      holderName: holder ? `${holder.firstName} ${holder.lastName}` : null,
      holderUpn: holder?.upn ?? null,
    };
    const existing = state.positions.findIndex((x) => x.positionId === positionId);
    if (existing >= 0) state.positions[existing] = record;
    else state.positions.push(record);
  }

  for (const r of roleOrgs) {
    const userId = r['USERID'] ?? '';
    const roleId = r['SECURITYROLEIDENTIFIER'] ?? '';
    const company = r['ORGANIZATIONID'] || companyId;
    if (
      !state.roleAssignments.some(
        (a) => a.userId === userId && a.roleId === roleId && a.companyId === company,
      )
    ) {
      state.roleAssignments.push({ userId, roleId, companyId: company });
    }
  }

  for (const l of limits) {
    const jobId = l['JOBID'] ?? '';
    const documentType = (l['DOCUMENTTYPE'] ?? '') as DocumentType;
    const amountUsd = Number(l['APPROVALLIMIT'] ?? 0);
    state.approvalLimits = state.approvalLimits.filter(
      (x) => !(x.companyId === companyId && x.jobId === jobId && x.documentType === documentType),
    );
    state.approvalLimits.push({ companyId, jobId, documentType, amountUsd });
  }
}
